// parseRequest.js -- server-side parser for the api_request JSON stored
// inside events.event_data.api_request (a stringified JSON).
//
// Output shape preserves the original block structure so the dashboard
// can render each content block as a distinct visual unit:
//
//   {
//     turns: [
//       { role, label, blocks: [
//           { type: 'text',        text } |
//           { type: 'tool_use',    id, name, input, facets? } |
//           { type: 'tool_result', tool_use_id, content, isError, linkedToolName? } |
//           { type: 'thinking',    thinking, signature? } |
//           { type: 'image',       source } |
//           { type: 'unknown',     raw }
//       ]},
//       ...
//     ],
//     tools: [string],
//     toolDefs: [{ name, description, schema }],
//     settings: { temperature, maxOutputTokens, stream, reasoningEffort }
//   }
//
// `toolDefs` keeps the FULL tool definitions the model was offered (name +
// description + input schema), alongside the name-only `tools` list. The MCP
// server detail lens reads these to show — and drift-check — what each server
// declares it can do (mcpServerDefs.js); nothing else needs them, so most
// callers keep destructuring `turns`/`tools`/`settings` and ignore it.
//
// Facets attach to the *originating* tool_use block only — never mirrored
// onto the matching tool_result. The result block carries `tool_use_id`
// and `linkedToolName` so the UI can show the back-reference without
// duplicating the chip set on the wrong turn.
//
// Per-block facets are parser-local display data. There is no canonical
// cross-trace validation source, so callers must not use them for filtering.

const SYSTEM_LABEL = "System";

const PATH_FIELDS = [
    "file_path", "path", "notebook_path",
    "original_filepath", "new_filepath", "filepath",
];

const URL_RE = /\bhttps?:\/\/([a-z0-9.\-]+)(?:[/:?][^\s'"`]*)?/gi;
const SHELL_PATH_RE = /(?:\.{1,2}\/|\/|[A-Za-z0-9_.][\w./\-]*\/)[\w./\-]+/g;

// Optimistic, format-tolerant extractor.
//
// Most upstreams emit valid JSON in either Anthropic Messages or OpenAI
// Chat Completions shape. A few proxies (groq adapter, etc.) emit a
// stringified-map format that JSON.parse rejects entirely. We try, in
// order:
//
//   1. JSON.parse → adaptMessage handles both Anthropic content-blocks
//      and OpenAI tool_calls / tool_call_id in one pass.
//   2. Relaxed regex extraction for non-JSON inputs — recovers role +
//      content from `{role:X,content:Y}` patterns.
//   3. Empty result.
//
// "Optimistic" here means: we never hard-fail because we didn't recognise
// the shape. Any extracted turns, however lossy, beat showing nothing.
/**
 * A tool result's text, whatever shape the provider sent it in.
 *
 * `tool_result.content` is a string on some providers and an array of content blocks
 * on others — Anthropic sends `[{type:"text",text}]` routinely — so a bare
 * `typeof content === "string"` reads an array-shaped result as EMPTY. That is the
 * worst failure available to anything that also reports an outcome: the record says
 * "answered" and carries nothing, which is exactly the shape a reader, or a model,
 * fills in for itself.
 *
 * Non-text blocks are dropped rather than stringified — an image or a nested tool
 * block has no text, and `[object Object]` in a result body is noise shaped like data.
 */
export function toolResultText(content) {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
        return content
            .filter((b) => b && b.type === "text" && typeof b.text === "string")
            .map((b) => b.text)
            .join("\n");
    }
    return "";
}

export function parseApiRequest(rawJson) {
    if (!rawJson) return emptyResult();

    // AI Studio web client sends `f.req=<URL-encoded-JSON>` instead of a
    // raw JSON object. Decode + regex out the user prompt so the panel
    // shows something readable; we don't try to reconstruct the full
    // turn list (the AI Studio JSON shape is bespoke and brittle).
    if (typeof rawJson === "string" && rawJson.startsWith("f.req=")) {
        return parseAIStudioFormReq(rawJson);
    }

    let req;
    try {
        req = JSON.parse(rawJson);
    } catch {
        // Some Gemini sources stash the request as a stringified map with
        // unquoted keys, single-quote-wrapped (e.g. `'{contents:[{parts:[
        // {text:hi}]}]}'`). Attempt to coerce that into JSON before
        // falling through to the line-noise relaxed extractor.
        const coerced = tryGeminiRelaxed(rawJson);
        if (coerced) {
            const r = parseGemini(coerced);
            if (r) return r;
        }
        return parseRelaxed(rawJson);
    }
    if (!req || typeof req !== "object") return parseRelaxed(rawJson);

    // OpenAI Responses API (codex / gpt-5.x): `{ type: 'response.create',
    // model, instructions, input: [...] }`. Item types include
    // `message`, `function_call`, `function_call_output`, `reasoning`,
    // `custom_tool_call`, `custom_tool_call_output`, `compaction`.
    if (req.type === "response.create" || Array.isArray(req.input)) {
        const r = parseOpenAIResponses(req);
        if (r) return r;
    }

    // Copilot Desktop: top-level `content: [{type, text}]` array, no
    // `messages[]`. One user turn per send.
    if (Array.isArray(req.content) && !Array.isArray(req.messages)) {
        const blocks = [];
        for (const b of req.content) {
            if (b?.type === "text" && typeof b.text === "string" && b.text.length) {
                blocks.push({ type: "text", text: b.text });
            }
        }
        if (blocks.length) {
            return {
                ...emptyResult(),
                turns: [{ role: "user", label: labelFor("user"), blocks }],
            };
        }
    }

    // Gemini's CLI / Code Assist envelope wraps the actual API call in
    // `{ model, project, user_prompt_id, request: { contents: [...] } }`.
    // Direct Gemini API calls may also use `contents` at the top level.
    // Either way the message structure is `parts[].text` keyed by `role`,
    // not Anthropic/OpenAI's `messages[]` shape — handle that branch here
    // before falling through to the messages-based parser.
    const geminiResult = parseGemini(req);
    if (geminiResult) return geminiResult;

    // Perplexity web (www.perplexity.ai /rest/sse/perplexity_ask): the user
    // prompt is a single top-level `query_str`; everything in `params` is
    // session metadata. There's no `messages[]`, so without this branch the
    // body parses to zero turns and the trace panel falls back to the RAW
    // blob (O-1600). Synthesize one user turn so the prompt renders. Guarded
    // on the absence of `messages[]` so a chat-shaped body that happens to
    // also carry `query_str` still parses its real turns.
    if (
        typeof req.query_str === "string" &&
        req.query_str &&
        !Array.isArray(req.messages)
    ) {
        return {
            ...emptyResult(),
            turns: [
                {
                    role: "user",
                    label: labelFor("user"),
                    blocks: [{ type: "text", text: req.query_str }],
                },
            ],
        };
    }

    // Consumer chat frontends post the user's message as a top-level scalar
    // `prompt` with NO `messages[]` — the conversation history lives
    // server-side, so each send carries only the new turn. Three provider
    // intercepts land here:
    //   * claude.ai web + Claude Desktop — POST /chat_conversations/<id>/completion
    //     (provider=anthropic): `{ prompt, model, attachments, files, ... }`
    //   * chat.deepseek.com — POST /api/v0/chat/completion (provider=deepseek):
    //     `{ prompt, chat_session_id, ... }`
    // Without this branch the body parses to zero turns and Trace Details falls
    // back to the RAW blob for all three (O-2458) — same failure mode the
    // Perplexity `query_str` branch above fixed in O-1600. Synthesize one user
    // turn so the prompt card renders. The same providers' SDK / SDK-compatible
    // endpoints (Anthropic /v1/messages, DeepSeek /v1/chat/completions) use
    // `messages[]` and parse through the loop below; the `!messages` guard lets
    // such a body win if it somehow also carries a stray top-level `prompt`.
    // `prompt` is a string for these surfaces, or — for the legacy OpenAI
    // /v1/completions shape — an array of strings.
    if (!Array.isArray(req.messages)) {
        const promptBlocks = promptToTextBlocks(req.prompt);
        if (promptBlocks.length) {
            return {
                ...emptyResult(),
                turns: [{ role: "user", label: labelFor("user"), blocks: promptBlocks }],
            };
        }
    }

    // Pre-pass: index every tool_use block by id. tool_result blocks
    // (Anthropic) and `tool` messages (OpenAI) reference back via
    // tool_use_id / tool_call_id, so this map both feeds chip attachment
    // on the originating call AND lets a result block surface
    // "this is a Bash result" without re-resolving the call.
    //
    // OpenAI tool_calls live on the assistant message itself rather than
    // as content blocks; we index those here too so adaptMessageBlocks
    // below can resolve linked names uniformly across both shapes.
    const toolUseIndex = new Map();
    for (const msg of req.messages ?? []) {
        if (!msg || typeof msg !== "object") continue;
        if (Array.isArray(msg.content)) {
            for (const block of msg.content) {
                if (!block || !USE_BLOCK_TYPES.has(block.type) || !block.id) continue;
                const name = toolUseName(block);
                toolUseIndex.set(block.id, {
                    name,
                    facets: extractToolUseFacets(name, block.input),
                });
            }
        }
        if (Array.isArray(msg.tool_calls)) {
            for (const tc of msg.tool_calls) {
                const id = tc?.id;
                const name = tc?.function?.name || tc?.name || "";
                if (!id || !name) continue;
                const input = parseOpenAIToolArguments(tc?.function?.arguments);
                toolUseIndex.set(id, {
                    name,
                    facets: extractToolUseFacets(name, input),
                });
            }
        }
    }

    const turns = [];

    // System prompt — string or array of TextBlocks. Both shapes flow
    // through blockFromContent so the renderer doesn't care which form
    // the upstream sent.
    const sys = req.system;
    if (typeof sys === "string" && sys.trim()) {
        turns.push({
            role: "system",
            label: SYSTEM_LABEL,
            blocks: [{ type: "text", text: sys }],
        });
    } else if (Array.isArray(sys) && sys.length > 0) {
        const blocks = sys.map((b) => blockFromContent(b, toolUseIndex)).filter(Boolean);
        if (blocks.length) {
            turns.push({ role: "system", label: SYSTEM_LABEL, blocks });
        }
    }

    for (const msg of req.messages ?? []) {
        if (!msg || typeof msg !== "object") continue;
        // ChatGPT desktop wire format puts the role under msg.author.role
        // instead of msg.role. Anthropic and OpenAI Chat use msg.role
        // directly. Default to user.
        const role = msg.role || msg.author?.role || "user";
        const blocks = adaptMessageBlocks(msg, toolUseIndex);
        if (blocks.length === 0) continue;
        turns.push({
            role,
            label: labelFor(role),
            blocks,
        });
    }

    const tools = Array.isArray(req.tools)
        ? req.tools.map((t) => t?.name || "(unnamed)").filter(Boolean)
        : [];

    const settings = {
        temperature: numOrZero(req.temperature),
        maxOutputTokens: numOrZero(req.max_tokens ?? req.max_output_tokens),
        stream: !!req.stream,
        reasoningEffort: stringifyReasoning(req),
    };

    return { turns, tools, toolDefs: toolDefsFrom(req.tools), settings };
}

function emptyResult() {
    return {
        turns: [],
        tools: [],
        toolDefs: [],
        settings: { temperature: 0, maxOutputTokens: 0, stream: false, reasoningEffort: "" },
    };
}

// Tool DEFINITIONS (name + description + schema) from a request's `tools`
// array — the surface the model was offered, kept verbatim alongside the
// name-only `tools` list. Handles Anthropic ({ name, description,
// input_schema }) and OpenAI ({ type:'function', function:{ name,
// description, parameters } }, or the flat variant). Gemini's nested
// functionDeclarations shape is built at its own call site.
function toolDefsFrom(list) {
    if (!Array.isArray(list)) return [];
    const out = [];
    for (const t of list) {
        const fn = t && typeof t === "object" && t.function ? t.function : t;
        const name = (fn && fn.name) || (t && t.name) || "";
        if (!name) continue;
        const description = typeof fn?.description === "string"
            ? fn.description
            : typeof t?.description === "string" ? t.description : "";
        const schema = fn?.input_schema ?? fn?.parameters ?? t?.input_schema ?? t?.parameters ?? null;
        out.push({ name, description, schema });
    }
    return out;
}

// One pass that handles BOTH Anthropic and OpenAI message shapes:
//
//   * Anthropic: msg.content is string OR array of typed blocks
//     (text / tool_use / tool_result / thinking / image).
//   * OpenAI:    msg.content is string (or null) plus an optional
//     `tool_calls` array on assistant messages, and `role: 'tool'`
//     messages with `tool_call_id` carrying tool results.
//
// We materialise both into the same internal block vocabulary so the
// renderer never has to know which API the upstream actually used.
function adaptMessageBlocks(msg, toolUseIndex) {
    const blocks = [];

    // Text-like content (shared between formats).
    if (typeof msg.content === "string" && msg.content.length) {
        blocks.push({ type: "text", text: msg.content });
    } else if (Array.isArray(msg.content)) {
        for (const b of msg.content) {
            const out = blockFromContent(b, toolUseIndex);
            if (out) blocks.push(out);
        }
    } else if (msg.content && typeof msg.content === "object") {
        // ChatGPT desktop wire format: `{content_type: "text", parts: [...]}`
        // (or "multimodal_text" with mixed parts). Pull text parts out.
        if (typeof msg.content.content_type === "string" && Array.isArray(msg.content.parts)) {
            for (const part of msg.content.parts) {
                if (typeof part === "string" && part.length) {
                    blocks.push({ type: "text", text: part });
                } else if (part && typeof part === "object") {
                    if (typeof part.text === "string" && part.text.length) {
                        blocks.push({ type: "text", text: part.text });
                    } else if (part.image_url || part.inline_image) {
                        blocks.push({ type: "image", source: part.image_url || part.inline_image });
                    }
                }
            }
        } else {
            blocks.push({ type: "unknown", raw: msg.content });
        }
    }

    // OpenAI assistant tool_calls — render alongside text content.
    if (Array.isArray(msg.tool_calls)) {
        for (const tc of msg.tool_calls) {
            const id = tc?.id || null;
            const name = tc?.function?.name || tc?.name || "";
            const input = parseOpenAIToolArguments(tc?.function?.arguments);
            const indexed = id ? toolUseIndex.get(id) : null;
            const block = { type: "tool_use", id, name, input };
            if (indexed) block.facets = setsToArrays(indexed.facets);
            blocks.push(block);
        }
    }

    // OpenAI tool result message: role='tool', tool_call_id, content=string.
    if (msg.role === "tool" && msg.tool_call_id) {
        const linked = toolUseIndex.get(msg.tool_call_id);
        const identity = validatedCopilotMcpIdentity(msg.mcp_identity, msg._origin, msg.tool_call_id, msg.name, linked?.name);
        // Replace any text-block we accumulated above with a tool_result —
        // the content is the function output, not a free-form chat turn.
        const resultContent = typeof msg.content === "string" ? msg.content : msg.content;
        return [{
            type: "tool_result",
            tool_use_id: msg.tool_call_id,
            content: resultContent,
            isError: msg.is_error === true,
            ...(identity ? { linkedToolName: msg.name, mcp_identity: identity }
                : linked?.name ? { linkedToolName: linked.name } : {}),
        }];
    }

    return blocks;
}

// Normalize a top-level `prompt` field into text blocks. Accepts the scalar
// string form (consumer chat frontends: claude.ai, Claude Desktop, DeepSeek
// web) and the legacy OpenAI /v1/completions array-of-strings form. Empty
// strings and non-string array elements are dropped, so a `prompt: ""` (or an
// all-empty array) yields no blocks and the caller stays at zero turns → RAW.
function promptToTextBlocks(prompt) {
    if (typeof prompt === "string") {
        return prompt ? [{ type: "text", text: prompt }] : [];
    }
    if (Array.isArray(prompt)) {
        return prompt
            .filter((p) => typeof p === "string" && p)
            .map((p) => ({ type: "text", text: p }));
    }
    return [];
}

function parseOpenAIToolArguments(args) {
    if (args == null) return null;
    if (typeof args === "object") return args;
    if (typeof args !== "string") return null;
    try { return JSON.parse(args); }
    catch { return args; }   // string-arg fallback (rare, but keep readable)
}

// OpenAI Responses API extractor. Codex sends prompts as
// `{ type: 'response.create', model, instructions, input: [...] }`,
// where `input[]` items are heterogeneous: messages, function calls,
// function call outputs, reasoning summaries, compactions. We map each
// item kind to our internal block vocabulary so the renderer doesn't
// need to know which OpenAI surface emitted it.
function parseOpenAIResponses(req) {
    const input = Array.isArray(req?.input) ? req.input : null;
    if (!input || input.length === 0) {
        // Edge case: response.create with no input items. Still emit a
        // system turn for the instructions so the panel isn't empty.
        const sys = typeof req?.instructions === "string" && req.instructions ? req.instructions : "";
        if (!sys) return null;
        return {
            ...emptyResult(),
            turns: [{ role: "system", label: SYSTEM_LABEL, blocks: [{ type: "text", text: sys }] }],
        };
    }

    // Pre-pass: index function/tool calls by call_id so corresponding
    // outputs can resolve their linked tool name.
    const callIndex = new Map();   // call_id → name
    for (const item of input) {
        if (!item || typeof item !== "object") continue;
        if ((item.type === "function_call" || item.type === "custom_tool_call") && item.call_id && item.name) {
            callIndex.set(item.call_id, item.name);
        }
    }

    const turns = [];

    // Top-level instructions become a leading system turn.
    if (typeof req.instructions === "string" && req.instructions.length) {
        turns.push({
            role: "system",
            label: SYSTEM_LABEL,
            blocks: [{ type: "text", text: req.instructions }],
        });
    }

    for (const item of input) {
        if (!item || typeof item !== "object") continue;

        if (item.type === "message") {
            const role = item.role || "user";
            const blocks = [];
            if (Array.isArray(item.content)) {
                for (const c of item.content) {
                    if (!c || typeof c !== "object") continue;
                    // Responses API uses input_text / output_text
                    // discriminators; both carry a plain `text` string.
                    if ((c.type === "input_text" || c.type === "output_text") && typeof c.text === "string") {
                        blocks.push({ type: "text", text: c.text });
                    } else if (c.type === "input_image") {
                        blocks.push({ type: "image", source: c.image_url || c });
                    } else if (typeof c.text === "string") {
                        blocks.push({ type: "text", text: c.text });
                    }
                }
            } else if (typeof item.content === "string") {
                if (item.content.length) blocks.push({ type: "text", text: item.content });
            }
            if (blocks.length === 0) continue;
            turns.push({ role, label: labelFor(role), blocks });
            continue;
        }

        if (item.type === "function_call" || item.type === "custom_tool_call") {
            const id = item.call_id || null;
            const name = item.name || "";
            const input_ = parseOpenAIToolArguments(item.arguments);
            turns.push({
                role: "assistant",
                label: labelFor("assistant"),
                blocks: [{ type: "tool_use", id, name, input: input_ }],
            });
            continue;
        }

        if (item.type === "function_call_output" || item.type === "custom_tool_call_output") {
            const tuid = item.call_id || null;
            const linked = tuid ? callIndex.get(tuid) : null;
            turns.push({
                role: "user",
                label: labelFor("user"),
                blocks: [{
                    type: "tool_result",
                    tool_use_id: tuid,
                    content: typeof item.output === "string" ? item.output : item.output,
                    isError: false,
                    ...(linked ? { linkedToolName: linked } : {}),
                }],
            });
            continue;
        }

        if (item.type === "reasoning") {
            const summaryParts = Array.isArray(item.summary) ? item.summary : [];
            const summaryText = summaryParts
                .map((s) => (s && typeof s.text === "string" ? s.text : ""))
                .filter(Boolean)
                .join("\n\n");
            const text = summaryText || (item.encrypted_content ? "[encrypted reasoning]" : "");
            if (!text) continue;
            turns.push({
                role: "assistant",
                label: labelFor("assistant"),
                blocks: [{ type: "thinking", thinking: text }],
            });
            continue;
        }

        if (item.type === "compaction") {
            // Encrypted conversation compaction — opaque payload. Note it
            // so the operator knows there was prior history rather than
            // dropping it silently.
            turns.push({
                role: "system",
                label: SYSTEM_LABEL,
                blocks: [{ type: "text", text: "[compacted prior conversation]" }],
            });
            continue;
        }

        if (item.type === "web_search_call") {
            // OpenAI Responses built-in web search. The query lives under
            // `action.query` (newer API) or a top-level `query`; synthesize a
            // tool_use so the web-research extractor sees it like the other
            // call types. Results come back as message annotations, not a
            // separate output item, so there's no paired result block.
            const q = item.action?.query || (typeof item.query === "string" ? item.query : "");
            turns.push({
                role: "assistant",
                label: labelFor("assistant"),
                blocks: [{ type: "tool_use", id: item.id || null, name: "web_search", input: q ? { query: q } : {} }],
            });
            continue;
        }

        // Unknown item type — surface as raw so the operator can see what
        // the upstream actually sent.
        turns.push({
            role: item.role || "assistant",
            label: labelFor(item.role || "assistant"),
            blocks: [{ type: "unknown", raw: item }],
        });
    }

    if (turns.length === 0) return null;

    const tools = Array.isArray(req.tools)
        ? req.tools.map((t) => t?.name || t?.function?.name || "(unnamed)").filter(Boolean)
        : [];

    return {
        turns,
        tools,
        toolDefs: toolDefsFrom(req.tools),
        settings: {
            temperature: numOrZero(req.temperature),
            maxOutputTokens: numOrZero(req.max_output_tokens ?? req.max_tokens),
            stream: !!req.stream,
            reasoningEffort: stringifyReasoning(req),
        },
    };
}

// Gemini extractor. The CLI / Code Assist envelope nests the real
// API request under `request`, so we accept either path. Inside, the
// shape is `contents: [{role, parts: [{...}]}]` where each part is one
// of: text, inlineData/fileData (image), functionCall (~tool_use),
// functionResponse (~tool_result). Roles map: model→assistant.
function parseGemini(req) {
    const contents = req?.request?.contents || req?.contents;
    if (!Array.isArray(contents) || contents.length === 0) return null;

    const turns = [];
    for (const msg of contents) {
        if (!msg || !Array.isArray(msg.parts)) continue;
        const rawRole = msg.role === "model" ? "assistant" : (msg.role || "user");
        const blocks = [];
        for (const part of msg.parts) {
            if (!part || typeof part !== "object") continue;
            if (typeof part.text === "string" && part.text.length) {
                blocks.push({ type: "text", text: part.text });
            } else if (part.functionCall) {
                blocks.push({
                    type: "tool_use",
                    id: null,
                    name: part.functionCall.name || "",
                    input: part.functionCall.args ?? part.functionCall.arguments ?? null,
                });
            } else if (part.functionResponse) {
                blocks.push({
                    type: "tool_result",
                    tool_use_id: null,
                    content: part.functionResponse.response ?? part.functionResponse,
                    isError: false,
                    ...(part.functionResponse.name ? { linkedToolName: part.functionResponse.name } : {}),
                });
            } else if (part.inlineData || part.fileData) {
                blocks.push({ type: "image", source: part.inlineData || part.fileData });
            } else if (part.thought && typeof part.thought === "string") {
                blocks.push({ type: "thinking", thinking: part.thought });
            }
        }
        if (blocks.length === 0) continue;
        turns.push({ role: rawRole, label: labelFor(rawRole), blocks });
    }
    if (turns.length === 0) return null;

    // Top-level systemInstruction shows up as a separate first turn in
    // the same shape as Anthropic's `system` field. Inserted at index 0
    // so the rendered timeline matches the actual prompt structure.
    const sys = req?.request?.systemInstruction || req?.systemInstruction;
    if (sys && Array.isArray(sys.parts)) {
        const sysBlocks = sys.parts
            .filter((p) => p && typeof p.text === "string" && p.text.length)
            .map((p) => ({ type: "text", text: p.text }));
        if (sysBlocks.length) {
            turns.unshift({ role: "system", label: SYSTEM_LABEL, blocks: sysBlocks });
        }
    }

    const tools = [];
    const toolDefs = [];
    const decls = req?.request?.tools || req?.tools;
    if (Array.isArray(decls)) {
        for (const t of decls) {
            const fns = t?.functionDeclarations || (t?.name ? [t] : []);
            if (Array.isArray(fns)) {
                for (const fn of fns) {
                    if (!fn?.name) continue;
                    tools.push(fn.name);
                    toolDefs.push({
                        name: fn.name,
                        description: typeof fn.description === "string" ? fn.description : "",
                        schema: fn.parameters ?? null,
                    });
                }
            }
        }
    }

    const cfg = req?.request?.generationConfig || req?.generationConfig || {};
    return {
        turns,
        tools,
        toolDefs,
        settings: {
            temperature: numOrZero(cfg.temperature),
            maxOutputTokens: numOrZero(cfg.maxOutputTokens),
            stream: false,
            reasoningEffort: "",
        },
    };
}

// Coerce a relaxed-stringified-map blob into a valid JSON object so
// parseGemini can chew on it. Targeted at shapes like
// `'{contents:[{role:user,parts:[{text:hi}]}]}'` (single-quote-wrapped,
// unquoted keys, bare-word values). Best-effort: returns null on
// anything that doesn't smell like Gemini-shaped to avoid corrupting
// other relaxed shapes that parseRelaxed already handles.
function tryGeminiRelaxed(raw) {
    if (typeof raw !== "string" || raw.length === 0) return null;
    let s = raw.trim();
    // Strip an outer single-quote wrapper.
    if (s.startsWith("'") && s.endsWith("'")) s = s.slice(1, -1).trim();
    // Only attempt this for blobs that look Gemini-shaped — `parts:` and
    // `text:` are characteristic. Skip if quotes already look balanced
    // around keys (i.e. already valid-ish JSON that happened to fail for
    // a different reason).
    if (!/parts\s*:/.test(s) || !/text\s*:/.test(s)) return null;
    if (/"\s*parts\s*"\s*:/.test(s)) return null;
    // Quote unquoted object keys.
    let fixed = s.replace(/([{,\[]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
    // Quote bare-identifier values (alphanum + . - _ tokens) when they
    // appear right after `:` and run up to a structural delimiter. Leave
    // numeric, true, false, null tokens unquoted.
    fixed = fixed.replace(
        /:\s*([A-Za-z_][A-Za-z0-9_\-./]*)\s*([,}\]])/g,
        (m, val, tail) => /^(true|false|null)$/.test(val) ? m : `:"${val}"${tail}`,
    );
    try { return JSON.parse(fixed); }
    catch { return null; }
}

// AI Studio form-data: `f.req=` URL-encoded JSON wrapping the prompt
// in escaped-quote literals. The first `\"...\"` capture in the decoded
// payload is the user's question; we emit it as a single user turn.
function parseAIStudioFormReq(raw) {
    let decoded;
    try { decoded = decodeURIComponent(raw); }
    catch { return emptyResult(); }
    const m = decoded.match(/\\"([^\\]+)\\"/);
    if (!m || !m[1]) return emptyResult();
    return {
        ...emptyResult(),
        turns: [{
            role: "user",
            label: labelFor("user"),
            blocks: [{ type: "text", text: m[1] }],
        }],
    };
}

// Relaxed extraction for non-JSON api_request shapes (e.g. groq adapter
// emits stringified maps with unquoted keys: `{model:X,messages:[{role:Y,
// content:Z},...]}`). We don't try to rebuild a full tree — just walk
// for {role:…,content:…} pairs and treat each as a text turn. Lossy on
// tool calls, but all we want here is to render *something* readable so
// the trace detail panel isn't a blank stare.
function parseRelaxed(raw) {
    if (typeof raw !== "string" || raw.length === 0) return emptyResult();

    const turns = [];
    // Match `role : VALUE , content : VALUE` where VALUE is a sequence
    // of non-`,}` chars (best-effort — this is a hostile parse anyway).
    // The double-pass anchor on `{` keeps us inside an object literal.
    const re = /\{\s*role\s*:\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*content\s*:\s*([^,}\]]+?)\s*[,}]/g;
    let m;
    while ((m = re.exec(raw)) !== null) {
        const role = m[1].trim();
        const content = m[2].trim().replace(/^['"]|['"]$/g, "");
        if (!role) continue;
        turns.push({
            role,
            label: labelFor(role),
            blocks: content ? [{ type: "text", text: content }] : [],
        });
    }

    return { ...emptyResult(), turns };
}

function labelFor(role) {
    if (role === "tool") return "TOOL";
    if (role === "assistant") return "Assistant";
    if (role === "user") return "User";
    if (role === "system") return SYSTEM_LABEL;
    return role[0]?.toUpperCase() + role.slice(1);
}

// The invoked tool's name for a use-block, normalized. A plain tool_use and a
// server_tool_use (web_search / code_execution) use their name as-is; the MCP
// connector's mcp_tool_use carries the server separately, so fold it into the
// mcp__server__action form the tool matchers key on — matching the client-side
// mcp__ naming convention. Shared by the toolUseIndex pre-pass and
// blockFromContent so the indexed name and the emitted block's name agree.
function toolUseName(b) {
    if (b?.type === "mcp_tool_use" && b.server_name) return `mcp__${b.server_name}__${b.name || ""}`;
    return b?.name || "";
}

// Content-block types that invoke a tool (and so carry a facet-bearing name +
// id worth indexing). Kept in sync with blockFromContent's use-block cases.
const USE_BLOCK_TYPES = new Set(["tool_use", "server_tool_use", "mcp_tool_use"]);

// Map a single Anthropic content block to our typed shape. tool_use
// blocks carry their pre-extracted facets (Set→Array converted) so the
// downstream renderers can display them without re-running the extractor.
// tool_result blocks carry the originating
// tool's name (looked up via tool_use_id) so the UI can show
// "Result · Bash" instead of an opaque tool_use_id.
function blockFromContent(b, toolUseIndex) {
    if (!b || typeof b !== "object") return null;
    switch (b.type) {
        case "text":
            return { type: "text", text: typeof b.text === "string" ? b.text : "" };
        case "tool_use": {
            const idx = b.id ? toolUseIndex.get(b.id) : null;
            const out = {
                type: "tool_use",
                id: b.id || null,
                name: b.name || "",
                input: b.input ?? null,
            };
            if (idx) out.facets = setsToArrays(idx.facets);
            return out;
        }
        case "tool_result": {
            const tuid = b.tool_use_id || null;
            const linked = tuid ? toolUseIndex.get(tuid) : null;
            return {
                type: "tool_result",
                tool_use_id: tuid,
                content: b.content,
                isError: !!b.is_error,
                ...(linked?.name ? { linkedToolName: linked.name } : {}),
            };
        }
        // Anthropic server-side tools (web_search, code_execution) and the
        // MCP connector use dedicated block types instead of plain
        // tool_use/tool_result. Normalize them to the same shape so every
        // tool extractor (Linear, web-research, …) sees them uniformly. An
        // mcp_tool_use carries its server separately, so fold it back into
        // the mcp__server__action name the matchers key on.
        case "server_tool_use":
        case "mcp_tool_use": {
            const idx = b.id ? toolUseIndex.get(b.id) : null;
            const out = { type: "tool_use", id: b.id || null, name: toolUseName(b), input: b.input ?? null };
            if (idx) out.facets = setsToArrays(idx.facets);
            return out;
        }
        case "web_search_tool_result":
        case "mcp_tool_result": {
            const tuid = b.tool_use_id || null;
            const linked = tuid ? toolUseIndex.get(tuid) : null;
            return {
                type: "tool_result",
                tool_use_id: tuid,
                content: b.content,
                isError: !!b.is_error,
                ...(linked?.name ? { linkedToolName: linked.name } : {}),
            };
        }
        case "thinking":
            return {
                type: "thinking",
                thinking: typeof b.thinking === "string" ? b.thinking : "",
                ...(b.signature ? { signature: b.signature } : {}),
            };
        case "image":
            return { type: "image", source: b.source ?? null };
        default:
            return { type: "unknown", raw: b };
    }
}

function setsToArrays(f) {
    return {
        files:     [...f.files],
        hosts:     [...f.hosts],
        skills:    [...f.skills],
        commands:  [...f.commands],
        urls:      [...f.urls],
        toolCalls: [...f.toolCalls],
    };
}

function numOrZero(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function stringifyReasoning(req) {
    const budget = req?.thinking?.budget_tokens;
    if (typeof budget === "number") return `${budget} tokens`;
    if (typeof req?.reasoning_effort === "string") return req.reasoning_effort;
    return "";
}

// Request-level generation controls, for DISPLAY in the trace-detail view only.
//
// Deliberately NOT folded into parseApiRequest's `settings`: that return is
// pinned byte-for-byte by the cross-language normalizer golden (see
// parseApiRequest.parity.test.js and skwach's test_normalizer_parity.py), so
// surfacing effort/speed there would force a golden regen plus a matching
// skwach change. Reading the raw body independently keeps this a UI-only add.
//
// `effort` is the graded control each provider carries on the wire:
//   - Anthropic / Claude Code: output_config.effort  (low|medium|high|xhigh|max)
//   - OpenAI Responses API:    reasoning.effort
//   - OpenAI Chat Completions: reasoning_effort
//   - legacy Anthropic extended thinking: thinking.budget_tokens → "N tokens"
// `speed` is "fast" when fast mode (Opus 4.8/4.7) was requested, else "".
// Values are the raw wire strings — no normalization.
export function requestControls(rawJson) {
    let req;
    try { req = JSON.parse(rawJson); } catch { return { effort: "", speed: "" }; }
    if (!req || typeof req !== "object") return { effort: "", speed: "" };
    const budget = req.thinking?.budget_tokens;
    const effort =
        (typeof req.output_config?.effort === "string" && req.output_config.effort) ||
        (typeof req.reasoning?.effort === "string" && req.reasoning.effort) ||
        (typeof req.reasoning_effort === "string" && req.reasoning_effort) ||
        (typeof budget === "number" ? `${budget} tokens` : "") ||
        "";
    const speed = req.speed === "fast" ? "fast" : "";
    return { effort, speed };
}

// ── Tool-use input → facet extraction ───────────────────────────────
//
// Per-tool input shape readers. Mirrors the canonical upstream
// former server-side extractor, but lighter: enough to
// label "this turn touched X" inline without re-deriving the full
// cross-trace index. There is no downstream validation — the detail
// panel renders these facets as-is (see the getTrace note in
// apiClient.js), so over-extraction lands directly as extra chips.
function extractToolUseFacets(toolName, input) {
    const out = {
        files:     new Set(),
        hosts:     new Set(),
        skills:    new Set(),
        commands:  new Set(),
        urls:      new Set(),
        toolCalls: new Set(),
    };
    if (toolName) out.toolCalls.add(toolName);
    if (!input || typeof input !== "object") return out;

    switch (toolName) {
        case "Bash": {
            const cmd = String(input.command ?? "");
            for (const h of pickHosts(cmd)) out.hosts.add(h);
            for (const u of pickUrls(cmd))  out.urls.add(u);
            for (const p of pickShellPaths(cmd)) out.files.add(p);
            for (const c of pickCommandVerbs(cmd)) out.commands.add(c);
            return out;
        }
        case "Read":
        case "Edit":
        case "Write":
        case "NotebookRead":
        case "NotebookEdit": {
            for (const f of PATH_FIELDS) {
                if (typeof input[f] === "string") out.files.add(input[f]);
            }
            return out;
        }
        case "Glob":
        case "Grep": {
            if (typeof input.path === "string") out.files.add(input.path);
            const pat = typeof input.pattern === "string" ? input.pattern : "";
            if (pat && (pat.includes("/") || pat.startsWith("*"))) out.files.add(pat);
            return out;
        }
        case "WebFetch": {
            const url = String(input.url ?? "");
            for (const h of pickHosts(url)) out.hosts.add(h);
            for (const u of pickUrls(url))  out.urls.add(u);
            return out;
        }
        case "Skill": {
            if (typeof input.skill === "string") out.skills.add(input.skill);
            return out;
        }
        default: {
            for (const f of PATH_FIELDS) {
                if (typeof input[f] === "string") out.files.add(input[f]);
            }
            return out;
        }
    }
}

function pickHosts(s) {
    const out = new Set();
    if (typeof s !== "string" || !s) return out;
    URL_RE.lastIndex = 0;
    let m;
    while ((m = URL_RE.exec(s)) !== null) {
        const host = (m[1] || "").toLowerCase();
        if (host) out.add(host);
    }
    return out;
}

function pickUrls(s) {
    const out = new Set();
    if (typeof s !== "string" || !s) return out;
    URL_RE.lastIndex = 0;
    let m;
    while ((m = URL_RE.exec(s)) !== null) {
        if (m[0]) out.add(m[0]);
    }
    return out;
}

// First-word command verb of each shell statement. Nothing filters false
// positives downstream — every verb extracted here renders as a chip — so
// the extractor must avoid mistaking embedded *program source* for shell
// statements. The two ways source leaks into a Bash command:
//
//   * Inline `-c`/`-e` scripts (`python -c "…"`, `node -e '…'`, `bash -c
//     "a; b"`): the whole script is a quoted argument. We split on the
//     statement separators (`&& || ; | newline`) only when OUTSIDE quotes
//     (splitShellStatements), so the script stays one statement and only
//     its interpreter (`python`) surfaces — not its `import`/`def`/`for`/
//     `print`/… lines (O-1807).
//   * Heredoc bodies (`python3 - <<'EOF' … EOF`, `cat <<EOF …`): the body
//     isn't quoted, so quote-awareness can't catch it — stripHeredocs
//     removes it before the split, leaving the opener command intact.
//
// Plus: assignment statements (`VERSION=1.2.3`) are skipped — the leading
// word is a variable, not a command — and the verb set is capped as a
// backstop against a pathological script.
const MAX_COMMAND_VERBS = 12;

function pickCommandVerbs(cmd) {
    const out = new Set();
    if (typeof cmd !== "string" || !cmd) return out;
    for (const stmt of splitShellStatements(stripHeredocs(cmd))) {
        if (out.size >= MAX_COMMAND_VERBS) break;
        const trimmed = stmt.trim();
        if (!trimmed) continue;
        const m = trimmed.match(/^[A-Za-z][A-Za-z0-9_\-]*\b/);
        if (!m) continue;
        // `word =`/`word=` is an assignment, not a command verb. (`==` is
        // never a shell statement start, so rejecting it costs nothing.)
        if (/^\s*=(?!=)/.test(trimmed.slice(m[0].length))) continue;
        out.add(m[0]);
    }
    return out;
}

// Quote-aware split on shell statement separators (`&& || ; | newline`).
// A separator inside a single- or double-quoted span does NOT split — so
// an inline script argument (`python -c "import x\nfor …"`) stays a single
// statement instead of being shredded into one bogus command per line.
// Single quotes are literal (no escapes); double quotes honour backslash
// escapes; a backslash outside quotes escapes the next char (line
// continuation). Unbalanced quotes (truncated traces) just run the tail
// into one statement — safe, since we only read each statement's first
// word.
function splitShellStatements(cmd) {
    const stmts = [];
    let buf = "";
    let quote = null;
    for (let i = 0; i < cmd.length; i++) {
        const ch = cmd[i];
        if (quote) {
            if (ch === "\\" && quote === '"' && i + 1 < cmd.length) {
                buf += ch + cmd[++i];
            } else {
                if (ch === quote) quote = null;
                buf += ch;
            }
            continue;
        }
        if (ch === "'" || ch === '"') { quote = ch; buf += ch; continue; }
        if (ch === "\\" && i + 1 < cmd.length) { buf += ch + cmd[++i]; continue; }
        if (ch === "\n" || ch === ";") { stmts.push(buf); buf = ""; continue; }
        if (ch === "|") { if (cmd[i + 1] === "|") i++; stmts.push(buf); buf = ""; continue; }
        if (ch === "&" && cmd[i + 1] === "&") { i++; stmts.push(buf); buf = ""; continue; }
        buf += ch;
    }
    if (buf) stmts.push(buf);
    return stmts;
}

// Drop heredoc bodies so an embedded script (`python3 -`, `cat <<EOF`, a
// `node <<'JS'` block, …) isn't shredded into one bogus command per line.
// The body starts on the line *after* the `<<DELIM` marker, so we keep
// the rest of the opener line (re-injected from capture group 3 — e.g.
// the `| wc -l` in `cat <<EOF | wc -l`) and drop from the first newline
// through the terminator line. Leaves the command that opened the heredoc
// (`python3 -`) and anything piped off its output intact. Matches the
// common forms: `<<EOF`, `<<'EOF'`, `<<"EOF"`, and indent-stripping
// `<<-EOF`. `[^\n]` (not `.`) keeps it CRLF-safe.
function stripHeredocs(cmd) {
    return cmd.replace(
        /<<-?[ \t]*(['"]?)([A-Za-z_]\w*)\1([^\n]*)\n(?:[^\n]*\n)*?[ \t]*\2[ \t]*\r?(?:\n|$)/g,
        (_m, _quote, _delim, rest) => ` ${rest} `,
    );
}

function pickShellPaths(cmd) {
    const out = new Set();
    if (typeof cmd !== "string" || !cmd) return out;
    URL_RE.lastIndex = 0;
    const stripped = cmd.replace(URL_RE, " ");
    SHELL_PATH_RE.lastIndex = 0;
    let m;
    while ((m = SHELL_PATH_RE.exec(stripped)) !== null) {
        const raw = m[0];
        if (!looksLikePath(raw)) continue;
        const normalised = raw.replace(/\/{2,}/g, "/").replace(/\/+$/, "");
        if (normalised) out.add(normalised);
    }
    return out;
}

function looksLikePath(s) {
    if (!s || typeof s !== "string") return false;
    if (s.length < 2 || s.length > 256) return false;
    if (/[\s"'`<>|;&$()]/.test(s)) return false;
    if (/^[a-z]+:\/\//i.test(s)) return false;
    const hasExtension = /\.[A-Za-z][A-Za-z0-9]{0,7}$/.test(s);
    if (!s.includes("/") && !hasExtension) return false;
    const isAbsolute = s.startsWith("/");
    const isRelative = s.startsWith("./") || s.startsWith("../");
    const segments = s.split("/").filter(Boolean);
    if (!hasExtension && !isAbsolute && !isRelative && segments.length < 3) {
        return false;
    }
    return true;
}

// Presentation enrichment operates on typed blocks without serializing a request.
export function decorateConversationTurns(turns) {
    const calls = new Map();
    for (const turn of turns) for (const block of turn.blocks || []) {
        if (block.type === "tool_use" && block.id) calls.set(block.id, block.name);
    }
    return turns.map((turn) => ({ ...turn, label: labelFor(turn.role), blocks: (turn.blocks || []).map((block) => {
        if (block.type === "tool_use") return { ...block, facets: setsToArrays(extractToolUseFacets(block.name, block.input)) };
        if (block.type === "tool_result") return { ...block, isError: Boolean(block.isError ?? block.is_error),
            linkedToolName: block.linkedToolName || block.tool_name || calls.get(block.tool_use_id) };
        return block;
    }) }));
}
import { validatedCopilotMcpIdentity } from "./copilotMcpIdentity.js";
